import { useTranslation } from "react-i18next";
import {
  Braces,
  CircleCheck,
  FileSpreadsheet,
  Pencil,
  Type,
  TriangleAlert,
  User,
} from "lucide-react";
import type {
  CampaignMessageCoverageEntry,
  CampaignVariableMapping,
  CampaignVariableMappingEntry,
} from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  mappingCoverageComplete,
  mappingEntryLabel,
  sourceTypeLabelKey,
  variableTokenLabel,
} from "./campaign-message-presentation.js";

function MappingSourceIcon({
  source,
}: {
  source: CampaignVariableMappingEntry["source"];
}): React.JSX.Element {
  switch (source) {
    case "CONTACT_FIELD":
      return <User size={14} aria-hidden="true" />;
    case "UPLOAD_COLUMN":
      return <FileSpreadsheet size={14} aria-hidden="true" />;
    case "CONSTANT":
      return <Type size={14} aria-hidden="true" />;
    default: {
      const _exhaustive: never = source;
      return _exhaustive;
    }
  }
}

export function CampaignCoverageIndicator({
  entry,
  label,
}: {
  entry: CampaignMessageCoverageEntry;
  label: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const complete = mappingCoverageComplete(entry);
  const pct =
    entry.totalReadyCount === 0
      ? 0
      : Math.round((entry.presentCount / entry.totalReadyCount) * 100);

  return (
    <div className="grid gap-1" data-testid={`campaign-coverage-${entry.parameterKey}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <p className="m-0 text-[0.7rem] font-medium text-muted">
          {t("campaigns.message.coverage")}
        </p>
        <p className="m-0 text-[0.75rem] font-medium tabular-nums">
          {t("campaigns.message.coverageCounts", {
            present: entry.presentCount,
            total: entry.totalReadyCount,
          })}
        </p>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-line/70" aria-hidden="true">
        <div
          className={cn("h-full rounded-full", complete ? "bg-success" : "bg-warning")}
          style={{ width: `${pct}%` }}
        />
      </div>
      {complete ? (
        <p className="m-0 flex items-center gap-1.5 text-[0.75rem] text-success">
          <CircleCheck size={12} aria-hidden="true" />
          {t("campaigns.message.coverageCompleteHint")}
        </p>
      ) : entry.missingCount > 0 ? (
        <div
          className="grid gap-0.5 rounded-md border border-warning/30 bg-warning-muted px-2 py-1.5"
          data-testid={`campaign-coverage-warning-${entry.parameterKey}`}
        >
          <p className="m-0 flex items-start gap-1.5 text-[0.75rem] font-medium text-warning">
            <TriangleAlert size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
            {t("campaigns.message.coverageMissingNamed", {
              count: entry.missingCount,
              label,
            })}
          </p>
          <p className="m-0 ps-5 text-[0.7rem] leading-snug text-warning">
            {t("campaigns.message.coverageExclusionHint")}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Compact inline list for required variables that have no clickable token in
 * the preview. Uses the same mapping dialog as preview tokens.
 */
export function CampaignPersonalizationSection({
  requiredKeys,
  mapping,
  canMutate,
  onMap,
}: {
  requiredKeys: readonly string[];
  mapping: CampaignVariableMapping;
  canMutate: boolean;
  onMap: (parameterKey: string, tokenId: string) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (requiredKeys.length === 0) return null;

  return (
    <section className="grid gap-1.5 pt-3" data-testid="campaign-personalization">
      <h3 className="m-0 flex items-center gap-1.5 text-[0.75rem] font-medium text-muted">
        <Braces size={12} aria-hidden="true" />
        {t("campaigns.content.additionalVariables")}
      </h3>
      <ul className="m-0 grid list-none gap-1 p-0">
        {requiredKeys.map((parameterKey) => {
          const entry = mapping.variables[parameterKey];
          const token = variableTokenLabel(parameterKey);
          const tokenId = `additional-${parameterKey}`;
          return (
            <li
              key={parameterKey}
              className="flex min-w-0 flex-wrap items-center gap-2 text-[0.8125rem]"
              data-testid={`campaign-variable-row-${parameterKey}`}
            >
              <LtrValue className="rounded-md border border-line/70 bg-surface-muted px-1.5 py-0.5 font-mono text-[0.75rem] font-semibold">
                {token}
              </LtrValue>
              {entry ? (
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 text-muted">
                    <MappingSourceIcon source={entry.source} />
                  </span>
                  <span className="min-w-0 truncate" dir="auto">
                    {t(sourceTypeLabelKey(entry.source))}
                    <span className="text-muted"> · </span>
                    {mappingEntryLabel(t, entry)}
                  </span>
                </span>
              ) : (
                <span className="text-[0.75rem] text-warning">{t("campaigns.message.unmapped")}</span>
              )}
              {canMutate ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2"
                  data-token-id={tokenId}
                  onClick={() => onMap(parameterKey, tokenId)}
                >
                  <Pencil size={12} aria-hidden="true" />
                  {entry ? t("campaigns.message.changeMapping") : t("campaigns.message.map")}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
